import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';
import { useUID } from 'react-uid';
import { twJoin, twMerge } from 'tailwind-merge';

import { noop } from 'lib/utils';

import Icon from '../Icon';
import Text from '../Text';

const getOptionsWithoutSelected = (options) =>
  options.filter(({ isSelected }) => !isSelected);

const findSelectedOption = (options) =>
  options.find(({ isSelected }) => isSelected);

const Select = forwardRef(
  (
    {
      className,
      defaultValue = null,
      disabled = false,
      error = false,
      full = false,
      id: customId,
      internalValueState = true,
      message,
      name,
      options = [],
      placeholder,
      required = false,
      selectClassName,
      selectWrapperClassName,
      support = false,
      topLabel,
      value = '',
      onBlur = noop,
      onChange = noop,
      onClick = noop,
      onFocus = noop,
      ...rest
    },
    ref
  ) => {
    const uniqueId = useUID();
    const id = customId || uniqueId;
    const [currValue, setCurrValue] = useState(
      internalValueState ? value : null
    );
    const selectedOption = findSelectedOption(options);
    const optionsWithoutSelected = getOptionsWithoutSelected(options);

    const onChangeHandler = (event) => {
      const { target } = event;

      onChange(event);

      if (internalValueState) {
        setCurrValue(target.value);
      }
    };

    return (
      <div
        className={twMerge(
          'flex w-fit',
          topLabel && 'flex-col',
          full && 'w-full',
          className
        )}
        {...rest}
      >
        {topLabel && (
          <label
            className={twJoin(
              'text-neutral-low-400 dark:text-neutral-high-200',
              topLabel && 'mb-2',
              support && 'flex items-center'
            )}
            htmlFor={id}
          >
            {topLabel}
            {required && '*'}
            {support && <Icon className="ml-1" name="help-sign" size="size4" />}
          </label>
        )}
        <div
          className={twJoin(
            'relative inline-flex w-full rounded-2 border focus:border-primary-100 dark:focus:border-primary-100',
            error && 'border-error-300 dark:border-error-200',
            disabled
              ? 'cursor-not-allowed border-neutral-high-500 bg-neutral-high-300 dark:border-neutral-low-100 dark:bg-neutral-low-300'
              : 'bg-neutral-high-100 dark:bg-neutral-low-300',
            !error &&
              !disabled &&
              'border-neutral-high-500 dark:border-neutral-low-100',
            selectWrapperClassName
          )}
        >
          <select
            ref={ref}
            className={twMerge(
              'z-1 w-full select-none text-ellipsis rounded-2 py-3 pl-4 pr-10 text-sm md:py-2 lg:pr-12 lg:text-base',
              'focus:outline-none focus:outline focus:outline-1 focus:outline-offset-1 focus:outline-primary-300 focus:ring-0 dark:focus:outline-primary-100',
              disabled
                ? 'cursor-not-allowed text-neutral-high-500 dark:text-neutral-low-100'
                : 'cursor-pointer text-neutral-low-300 dark:text-neutral-high-200',
              full && 'w-full',
              selectClassName
            )}
            defaultValue={defaultValue || undefined}
            disabled={disabled}
            id={id}
            name={name}
            required={required}
            value={internalValueState ? currValue : value}
            suppressHydrationWarning
            onBlur={(event) => onBlur(event)}
            onChange={(event) => onChangeHandler(event)}
            onClick={(event) => onClick(event)}
            /**
             * Is used to suppress errors related of inconsistencies between
             * client-side and server-side of ids generated by 'react-uid'
             */
            onFocus={(event) => onFocus(event)}
          >
            {((internalValueState && !value) || !internalValueState) &&
              (selectedOption ? (
                <option
                  className="bg-neutral-high-100 dark:bg-neutral-low-300"
                  value={selectedOption.value}
                >
                  {selectedOption.label}
                </option>
              ) : (
                <option
                  className="bg-neutral-high-100 dark:bg-neutral-low-300"
                  value=""
                >
                  {placeholder}
                </option>
              ))}
            {optionsWithoutSelected.length > 0 &&
              optionsWithoutSelected.map(({ label, value: optionValue }) => (
                <option
                  key={optionValue}
                  className="bg-neutral-high-100 dark:bg-neutral-low-300"
                  value={optionValue}
                >
                  {label}
                </option>
              ))}
          </select>
          <Icon
            className={twJoin(
              'absolute right-4 top-1/2 z-10 -translate-y-1/2',
              disabled && 'dark:text-neutral-low-100'
            )}
            name="keyboard-arrow-down"
            size="size2"
          />
        </div>
        {message && (
          <Text
            className={twJoin(
              'relative mt-1 block text-sm lg:text-base',
              error
                ? 'text-error-300 dark:text-error-200'
                : 'text-neutral-low-100 dark:text-neutral-high-200'
            )}
          >
            {message}
          </Text>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

Select.propTypes = {
  /**
   * Options to be rendered
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      isSelected: PropTypes.bool,
    })
  ).isRequired,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Default value for uncontrolled components
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Whether the input field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the input field has an error
   */
  error: PropTypes.bool,
  /**
   * Button fills the parent component's width
   */
  full: PropTypes.bool,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Custom identifier for the select input. If this is not provided,
   *  we will add a unique id with the prefix `select_`
   */
  id: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Control whether or not to create and manage value internal state.
   */
  internalValueState: PropTypes.bool,
  /**
   * Support message to be rendered below the select field
   */
  message: PropTypes.string,
  /**
   * Receives HTML attr `name` value
   */
  name: PropTypes.string,
  /**
   * Text to be rendered as a label above
   */
  topLabel: PropTypes.string,
  /**
   * Renders a support icon if the `topLabel` prop is present
   */
  support: PropTypes.bool,
  /**
   * Text that appears in the select when no option was selected
   */
  placeholder: PropTypes.string,
  /**
   * Whether select has to contain a value to be submittable, also adds a * if
   * the `topLabel` prop is present
   */
  required: PropTypes.bool,
  /**
   * Value of the select
   */
  value: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on blur
   */
  onBlur: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on change
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on focus
   */
  onFocus: PropTypes.func,
};

export default Select;
